import { select, call, takeLatest, put } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { StudentActions } from '../constants';
import { StudentApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteStudentAction,
    GetStudentAction,
    GetStudentsAction,
    PersistStudentAction,
    CreateFilesStudentAction,
    GetFilesAction,
    DeleteFileStudentAction,
    GetIntakesAction,
    CreateIntakesStudentAction, getIntakes,
} from '../actions/student-actions';
import sortingService from '../../services/sorting';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { convertValuesToFormData } from '../../components/redux-form/utils/values-to-form-data';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { GetProvidersSmallAction, PersistProviderAction } from '../actions/provider-actions';
import { changeToZeroMoment, changeToZeroMomentDate } from '../../helpers/timeHelpers';
import { exportToCsv } from 'src/services/exports/csv';
import { GetExpensesAction } from '../actions/expense-actions';
import { groupInvoiceToCsv } from '../../helpers/invoice/groupCsv';
import { groupStudentToCsv } from '../../helpers/student/groupCsv';

function* handleFetchStudents(action: GetStudentsAction) {
    const oldData = yield select((state: StoreState) => state.students.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: AnyObject = { 'order[firstName]': 'asc', ...filters };
    if (filters?.createdAt) {
        filterProps = {
            'order[firstName]': 'asc',
            ...filters,
            'createdAt[before]': changeToZeroMomentDate(`${filters.createdAt}`),
            'createdAt[after]': changeToZeroMomentDate(`${filters.createdAt}`),
        };
        delete filterProps.createdAt;
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        StudentApi.getStudents({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}
function* handleFetchStudentsSmall(action: GetProvidersSmallAction) {
    const oldData = yield select((state: StoreState) => state.students.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        StudentApi.getStudentsSmall({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchStudent(action: GetStudentAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => StudentApi.getStudent(id));
}

function* handleDeleteStudent(action: DeleteStudentAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => StudentApi.deleteStudent(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistStudent(action: PersistStudentAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        files,
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => (id ? StudentApi.updateStudent(id, restData) : StudentApi.createStudent(restData)),
        function* (data: ResolverApiSuccess) {
            const actionFiles: CreateFilesStudentAction = {
                type: StudentActions.CREATE_FILES_STUDENT,
                payload: {
                    id: data.payload.id,
                    requestType: 'OTHER',
                    files: files.files,
                    onSuccess: () => false,
                    onError: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            };
            const actionOrders: CreateFilesStudentAction = {
                type: StudentActions.CREATE_FILES_STUDENT,
                payload: {
                    id: data.payload.id,
                    requestType: 'ORDER',
                    files: files.orderDoc ? files.orderDoc : [],
                    onSuccess: () => false,
                    onError: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            };
            const actionConsent: CreateFilesStudentAction = {
                type: StudentActions.CREATE_FILES_STUDENT,
                payload: {
                    id: data.payload.id,
                    requestType: 'CONSENT',
                    files: files.consentDoc ? files.consentDoc : [],
                    onSuccess: () => false,
                    onError: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            };
            const actionParentConsent: CreateFilesStudentAction = {
                type: StudentActions.CREATE_FILES_STUDENT,
                payload: {
                    id: data.payload.id,
                    requestType: 'PARENT_CONSENT',
                    files: files.parentConsentDoc ? files.parentConsentDoc : [],
                    onSuccess: () => false,
                    onError: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            };
            yield call(callbacks.resolve);

            yield call(handleCreateFilesStudent, actionFiles);
            yield call(handleCreateFilesStudent, actionConsent);
            yield call(handleCreateFilesStudent, actionParentConsent);
            yield call(handleCreateFilesStudent, actionOrders);
            if (!id) {
                yield put(push(Router.generate(routes.STUDENTS_LIST)));
            } else {
                yield put(push(Router.generate(routes.STUDENT_DETAIL, { id, tab: 'student-info' })));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            yield handleError(result);
        },
    );
}

function* handleGetFilesStudent(action: GetFilesAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => StudentApi.getFilesStudent(id));
}

function* handleGetIntakesStudent(action: GetIntakesAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => StudentApi.getIntakesStudent(id));
}

function* handleCreateFilesStudent(action: CreateFilesStudentAction) {
    const {
        payload: { id, requestType, files, onSuccess, onError },
    } = action;

    let requestFunction: any = (file: any) => {
        const objToForm = { student: id, attachFile: file };
        const formData = convertValuesToFormData(objToForm);
        return StudentApi.createFilesStudent(formData);
    };

    switch (requestType) {
        case 'ORDER':
            requestFunction = (file: any) => {
                const objToForm = { orderDocFile: file };
                const formData = convertValuesToFormData(objToForm, '');

                return StudentApi.createOrderDocStudent(formData, id);
            };
            break;
        case 'CONSENT':
            requestFunction = (file: any) => {
                const objToForm = { consentDocFile: file };
                const formData = convertValuesToFormData(objToForm, '');
                return StudentApi.createConsentDocStudent(formData, id);
            };
            break;
        case 'PARENT_CONSENT':
            requestFunction = (file: any) => {
                const objToForm = { parentConsentDocFile: file };
                const formData = convertValuesToFormData(objToForm, '');
                return StudentApi.createParentConsentDocStudent(formData, id);
            };
            break;
        default:
            requestFunction = (file: any) => {
                const objToForm = { student: id, attachFile: file };
                const formData = convertValuesToFormData(objToForm, '');
                return StudentApi.createFilesStudent(formData);
            };
            break;
    }

    for (let i = 0; i < files.length; i++) {
        if (files[i].id || files[i]?.attach || !files[i].type) {
            continue;
        }
        yield resolveApiCall(
            action,
            () => requestFunction(files[i]),
            function (data: ResolverApiSuccess) {
                if (onSuccess) {
                    onSuccess();
                }
                // yield call(callbacks.resolve);
            },
            function (result: ResolverApiFailure) {
                if (result.error && result.error.response && result.error.response.status) {
                    if (onError) {
                        onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                    }
                }
            },
        );
    }
}

function* handleCreateIntakesStudent(action: CreateIntakesStudentAction) {
    const {
        payload: { id, requestType, files, setLoad, onSuccess, onError },
    } = action;

    const requestFunction: any = (file: any) => {
        const objToForm = { student: id, attachFile: file, intakeType: requestType };
        const formData = convertValuesToFormData(objToForm);
        return StudentApi.createIntakeStudent(formData, id, requestType);
    };

    // DropZoneField returns an array when there is no attached file yet, and a single File otherwise.
    // The endpoint accepts one binary file, so always pass the last picked File.
    const file = Array.isArray(files) ? files[files.length - 1] : files;

    if (!file) {
        if (setLoad) {
            setLoad(false);
        }
        if (onError) {
            onError('No file selected');
        }
        return;
    }

    yield resolveApiCall(
        action,
        () => requestFunction(file),
        function* (data: ResolverApiSuccess) {
            if (onSuccess) {
                onSuccess();
            }
            yield put(getIntakes(id));
            // yield call(callbacks.resolve);
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
        },
    );
}

function* handleFetchStudentsCsv(action: GetStudentsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;
    let filterProps: any = { ...filters };
    if (filters?.createdAt) {
        filterProps = {
            ...filters,
            'createdAt[before]': changeToZeroMomentDate(`${filters.createdAt}`),
            'createdAt[after]': changeToZeroMomentDate(`${filters.createdAt}`),
        };
        delete filterProps.createdAt;
    }
    if (filterProps?.periodStart) {
        const periodStart = changeToZeroMoment(`${filterProps?.periodStart['periodStart[after]']}`, 'YYYY-MM-DD');
        const periodEnd = changeToZeroMoment(`${filterProps.periodStart['periodStart[before]']}`, 'YYYY-MM-DD');

        filterProps = {
            ...filterProps,
            'periodStart[after]': periodStart,
            'periodStart[before]': periodEnd,
        };
        delete filterProps.periodStart;
    }

    yield resolveApiCall(
        action,
        () =>
            StudentApi.getStudents({
                ...filterProps,
                order: sortingService.makeOrder(sorting),
                page,

                itemsPerPage: 5000,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetExpensesAction = {
                    type: StudentActions.FETCH_STUDENTS_CSV,
                    payload: {
                        ...filterProps,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchStudentsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupStudentToCsv(dataAll);
                    exportToCsv('students.csv', groupCsv);
                } else {
                    const groupCsvData = groupStudentToCsv(data.payload);
                    exportToCsv('students.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleDeleteFileStudent(action: DeleteFileStudentAction) {
    const {
        payload: { id, requestType, onSuccess, onError },
    } = action;

    let requestFunction = StudentApi.deleteFileStudent;
    switch (requestType) {
        case 'ORDER':
            requestFunction = StudentApi.deleteFileStudent;
            break;
        case 'CONSENT':
            requestFunction = StudentApi.deleteFileStudent;
            break;
        case 'PARENT_CONSENT':
            requestFunction = StudentApi.deleteFileStudent;
            break;
        default:
            requestFunction = StudentApi.deleteFileStudent;
            break;
    }

    yield resolveApiCall(
        action,
        () => requestFunction(id),
        function (data) {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handleUpdateStudent(action: PersistProviderAction) {
    const { payload } = action;
    const { data, callbacks } = payload;

    yield resolveApiCall(
        action,
        () => StudentApi.patchStudent(data),
        function* () {
            yield call(callbacks.resolve);
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                return yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                          }),
                );
            }
            yield handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(StudentActions.FETCH_STUDENTS, handleFetchStudents);
    yield takeLatest(StudentActions.FETCH_STUDENTS_CSV, handleFetchStudentsCsv);
    yield takeLatest(StudentActions.FETCH_STUDENTS_SIMPLY, handleFetchStudentsSmall);
    yield takeLatest(StudentActions.FETCH_STUDENT, handleFetchStudent);
    yield takeLatest(StudentActions.DELETE_STUDENT, handleDeleteStudent);
    yield takeLatest(StudentActions.PERSIST_STUDENT, handlePersistStudent);
    yield takeLatest(StudentActions.DELETE_FILE_STUDENT, handleDeleteFileStudent);
    yield takeLatest(StudentActions.CREATE_FILES_STUDENT, handleCreateFilesStudent);
    yield takeLatest(StudentActions.CREATE_INTAKE_STUDENT, handleCreateIntakesStudent);
    yield takeLatest(StudentActions.FETCH_FILES_STUDENT, handleGetFilesStudent);
    yield takeLatest(StudentActions.FETCH_INTAKES_STUDENT, handleGetIntakesStudent);
    yield takeLatest(StudentActions.UPDATE_STUDENT, handleUpdateStudent);
}
